import React, { useState } from 'react';
import { Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { FaWrench, FaTimes, FaCheckCircle, FaClock, FaSpinner } from 'react-icons/fa';
import Spinner from '../Spinner';

const QuickEditSection = ({
  showQuickEditSection,
  quickEditInstructions,
  includeLambdaLogs,
  includeExecutionAnalysis,
  submittingQuickEdit,
  quickEditResult,
  lambdaLogs,
  analysisResult,
  agent,
  parentAgent,
  quickEditReferenceAgent = '',
  onReferenceAgentChange,
  onInstructionsChange,
  onIncludeLambdaLogsChange,
  onIncludeExecutionAnalysisChange,
  onSubmit,
  onClose
}) => {
  const [referenceAgentError, setReferenceAgentError] = useState('');

  if (!showQuickEditSection) return null;

  const handleReferenceAgentChange = (value) => {
    const sanitized = value.replace(/[^0-9]/g, '').slice(0, 3);
    onReferenceAgentChange(sanitized);

    if (sanitized === '') {
      setReferenceAgentError('');
    } else if (sanitized.length > 0 && sanitized.length < 3) {
      setReferenceAgentError('Must be exactly 3 digits (e.g., 101, 205)');
    } else if (sanitized.length === 3 && !/^[0-9]{3}$/.test(sanitized)) {
      setReferenceAgentError('Must be a valid 3-digit number (e.g., 101, 205)');
    } else {
      setReferenceAgentError('');
    }
  };

  const isReferenceAgentValid = quickEditReferenceAgent === '' || /^[0-9]{3}$/.test(quickEditReferenceAgent);
  const isFormValid = quickEditInstructions.trim().length >= 10 && isReferenceAgentValid;

  return (
    <Card className="logs-card mb-4">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="section-heading">
            <FaWrench className="me-2 text-success" />
            Quick Edit / Fix
            {submittingQuickEdit && <FaSpinner className="fa-spin ms-2" />}
            {quickEditResult?.success && !submittingQuickEdit && (
              <Badge bg="success" className="ms-2">Complete</Badge>
            )}
            {quickEditResult && !quickEditResult.success && !submittingQuickEdit && (
              <Badge bg="danger" className="ms-2">Failed</Badge>
            )}
          </h2>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={onClose}
            disabled={submittingQuickEdit}
          >
            <FaTimes />
          </Button>
        </div>
      </Card.Header>
      <Card.Body style={{ minHeight: '300px', maxHeight: '70vh', overflowY: 'auto' }}>
        {quickEditResult?.success && (
          <Alert variant="success" className="mb-3">
            <FaCheckCircle className="me-2" />
            {quickEditResult.message}
            {quickEditResult.executionArn && (
              <div className="mt-2">
                <small className="text-muted">
                  Execution ARN: {quickEditResult.executionArn}
                </small>
              </div>
            )}
          </Alert>
        )}

        {quickEditResult && !quickEditResult.success && (
          <Alert variant="danger" className="mb-3">
            <strong>Error:</strong> {quickEditResult.error}
          </Alert>
        )}
        
        {submittingQuickEdit && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center">
              <FaClock className="me-2" />
              <div>
                <strong>Processing your request...</strong>
                <div className="small">
                  Executing quick edit/fix instructions. This may take a few moments to complete.
                </div>
              </div>
            </div>
          </Alert>
        )}
        
        {!quickEditResult?.success && !submittingQuickEdit && (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Instructions <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={quickEditInstructions}
                onChange={(e) => onInstructionsChange(e.target.value)}
                placeholder="Enter detailed instructions for what changes to make to the agent..."
                disabled={submittingQuickEdit}
                required
              />
              <Form.Text className="text-muted">
                {quickEditInstructions.length}/10 characters minimum
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Reference Agent <span className="quick-edit-optional-label">(optional)</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={quickEditReferenceAgent}
                onChange={(e) => handleReferenceAgentChange(e.target.value)}
                placeholder="e.g., 101"
                maxLength={3}
                disabled={submittingQuickEdit}
                isInvalid={!!referenceAgentError}
                className="quick-edit-reference-agent-input"
              />
              <Form.Text className="text-muted">
                A 3-digit agent number to use as reference guidance for the edit
              </Form.Text>
              {referenceAgentError && (
                <Form.Control.Feedback type="invalid">
                  {referenceAgentError}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Include Lambda logs"
                checked={includeLambdaLogs}
                onChange={(e) => onIncludeLambdaLogsChange(e.target.checked)}
                disabled={submittingQuickEdit || !lambdaLogs}
              />
              <Form.Text className="text-muted">
                {!lambdaLogs && 'Lambda logs must be fetched first to use this option'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Include Execution analysis with AI"
                checked={includeExecutionAnalysis}
                onChange={(e) => onIncludeExecutionAnalysisChange(e.target.checked)}
                disabled={submittingQuickEdit || !analysisResult?.agentOutput}
              />
              <Form.Text className="text-muted">
                {!analysisResult?.agentOutput && 'Execution analysis must be completed first to use this option'}
              </Form.Text>
            </Form.Group>
            
            {agent && parentAgent && (
              <Alert variant="info" className="mb-3">
                <h6>Target Information:</h6>
                <div><strong>Agent Number:</strong> {agent.agentNumber}</div>
                <div><strong>Agent Name:</strong> {agent.name}</div>
                <div><strong>Parent Agent:</strong> {parentAgent.name}</div>
                <div><strong>GitHub Repository:</strong> {parentAgent.repoAccount}/{parentAgent.repoName}</div>
              </Alert>
            )}

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                onClick={onSubmit}
                disabled={submittingQuickEdit || !isFormValid}
              >
                {submittingQuickEdit ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Executing...
                  </>
                ) : (
                  <>
                    <FaWrench className="me-2" />
                    Submit Quick Edit / Fix
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}

        {quickEditResult?.success && !submittingQuickEdit && (
          <div className="text-center py-4">
            <FaCheckCircle size={48} className="text-success mb-3" />
            <h5 className="text-success mb-3">Request Processed Successfully!</h5>
            <p className="lead mb-3">{quickEditResult.message}</p>
            {quickEditResult.executionArn && (
              <div className="mb-3">
                <small className="text-muted">
                  Execution ARN: {quickEditResult.executionArn}
                </small>
              </div>
            )}
            <div className="text-muted">
              <small>This section will close automatically in a few moments...</small>
            </div>
          </div>
        )}

        {!quickEditResult && !submittingQuickEdit && !quickEditInstructions.trim() && (
          <div className="text-center py-4">
            <FaWrench size={48} className="text-muted mb-3" />
            <p>Provide detailed instructions for what changes or fixes you'd like to make to this agent.</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default QuickEditSection;
